import { useEffect, useMemo, useRef, useState } from "react";
import { useGameStore } from "../store/useGameStore";
import { HeroCard } from "../components/HeroCard";
import { EquipmentPanel } from "../components/EquipmentPanel";
import { ItemDetailModal } from "../components/ItemDetailModal";
import { EquipCandidatesList } from "../components/EquipCandidatesList";
import { heroApi } from "../api/hero";
import { inventoryApi } from "../api/inventory";
import { astralApi } from "../api/astral";
import { getHeroImagePath, getSkillIconPath, resolveSkillDisplay } from "../utils/assets";
import { SkillIcon } from "../components/SkillIcon";
import { AdminAssetPath } from "../components/AdminAssetPath";
import { AstralChip } from "../components/AstralDetailModal";
import type { AstralState, EquippedSlot, InventoryOverview, SkillName } from "../types";
import clsx from "clsx";
import { FormattedNumber } from "../components/FormattedNumber";
import { RenameHeroModal } from "../components/RenameHeroModal";
import { PlayerNameBadges } from "../components/PlayerNameBadges";
import { AutoBattleSettingsPanel } from "../components/AutoBattleModeModal";
import type { AutoBattlePrefsPayload } from "../components/AutoBattleModeModal";
import { resolveAutoBattlePrefs, resolveAutoBattleEntitlements } from "../utils/autoBattle";
import { useBlacksmithStore } from "../store/useBlacksmithStore";

const DEFAULT_ROTATION_SKILLS: SkillName[] = [
  "SKILL_1",
  "SKILL_2",
  "ULT",
  "SKILL_4",
  "SKILL_5",
  "SKILL_6",
  "SKILL_7",
  "SKILL_8",
];

export function HeroScreen() {
  const { hero, refreshHero, refreshBalance, balance, setScreen, skillNames, refreshSkillNames } = useGameStore();
  const { config: blacksmithConfig, refresh: refreshBlacksmith } = useBlacksmithStore();
  const [editingRotation, setEditingRotation] = useState(false);
  const [rotation, setRotation] = useState<SkillName[]>(hero?.rotation ?? ["SKILL_1", "SKILL_1"]);
  const [saving, setSaving] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [overview, setOverview] = useState<InventoryOverview | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<EquippedSlot | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [astralState, setAstralState] = useState<AstralState | null>(null);
  const autoBattleSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inventoryApi.getOverview().then((o) => setOverview(o)).catch(() => {});
    void refreshBlacksmith();
  }, [hero?.level, refreshBlacksmith]);

  const equipped = overview?.equipped ?? [];

  useEffect(() => {
    if ((hero?.level ?? 0) >= 25) {
      astralApi.getState().then(setAstralState).catch(() => setAstralState(null));
    } else {
      setAstralState(null);
    }
  }, [hero?.level]);

  useEffect(() => {
    void refreshSkillNames();
  }, [refreshSkillNames, hero?.hero_class]);

  useEffect(() => {
    if (!editingRotation && hero?.rotation) {
      setRotation(hero.rotation);
    }
  }, [hero?.rotation, editingRotation]);

  const availableSkills = useMemo(() => {
    const fromApi = Object.keys(skillNames).filter((k) => k !== "BASIC") as SkillName[];
    if (fromApi.length > 0) return fromApi;
    return DEFAULT_ROTATION_SKILLS;
  }, [skillNames]);

  const rotationPickerSkills = useMemo(() => {
    const levels = hero?.skill_levels ?? {};
    return availableSkills.filter((skill) => (levels[skill] ?? 0) >= 1);
  }, [availableSkills, hero?.skill_levels]);

  const skillLabel = (skill: SkillName) => resolveSkillDisplay(skill, skillNames);

  const applyInventory = async (
    fn: () => Promise<InventoryOverview>,
    opts?: { keepSlot?: string },
  ) => {
    setActionLoading(true);
    try {
      const data = await fn();
      setOverview(data);
      await Promise.all([refreshHero(), refreshBalance()]);
      if (opts?.keepSlot) {
        const slot = data.equipped.find((s) => s.slot === opts.keepSlot);
        setSelectedSlot(slot ?? null);
      } else {
        setSelectedSlot(null);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const applyUpgrade = async (itemId: number, luckLevel: number, keepSlot: string) => {
    setActionLoading(true);
    try {
      const res = await inventoryApi.upgrade(itemId, luckLevel);
      setOverview(res.inventory);
      await Promise.all([refreshHero(), refreshBalance()]);
      const slot = res.inventory.equipped.find((s) => s.slot === keepSlot);
      setSelectedSlot(slot ?? null);
    } finally {
      setActionLoading(false);
    }
  };

  const persistAutoBattle = useMemo(
    () => (prefs: AutoBattlePrefsPayload) => {
      const current = useGameStore.getState().hero;
      if (current) {
        useGameStore.setState({
          hero: { ...current, auto_battle: prefs },
        });
      }
      if (autoBattleSaveRef.current) clearTimeout(autoBattleSaveRef.current);
      autoBattleSaveRef.current = setTimeout(() => {
        void heroApi.updateAutoBattle(prefs).then(() => refreshHero());
      }, 300);
    },
    [refreshHero],
  );

  useEffect(
    () => () => {
      if (autoBattleSaveRef.current) clearTimeout(autoBattleSaveRef.current);
    },
    [],
  );

  if (!hero) return <div className="p-4 text-gray-400 text-center py-12">Loading hero...</div>;

  const autoBattle = resolveAutoBattlePrefs(hero);

  const slotCapacity = hero.rotation_slots ?? 16;
  const slotsMax = hero.rotation_slots_max ?? 32;
  const upgradeCost = hero.rotation_slot_upgrade_cost_diamonds ?? 5;
  const upgradeIncrement = hero.rotation_slot_upgrade_increment ?? 4;
  const canExpandSlots = slotCapacity < slotsMax;
  const diamonds = balance?.diamonds ?? 0;
  const canAffordExpand = diamonds >= upgradeCost;

  const addToRotation = (skill: SkillName) => {
    if (rotation.length >= slotCapacity) return;
    setRotation([...rotation, skill]);
  };

  const removeFromRotation = (idx: number) => {
    setRotation(rotation.filter((_, i) => i !== idx));
  };

  const saveRotation = async () => {
    setSaving(true);
    try {
      await heroApi.updateRotation(rotation);
      await refreshHero();
      setEditingRotation(false);
    } finally {
      setSaving(false);
    }
  };

  const expandRotationSlots = async () => {
    setExpanding(true);
    try {
      await heroApi.expandRotationSlots();
      await Promise.all([refreshHero(), refreshBalance()]);
    } finally {
      setExpanding(false);
    }
  };

  const heroImageSrc = getHeroImagePath(hero.hero_class, hero.gender);

  return (
    <div className="space-y-0">
      <div className="bg-gradient-to-r from-kingdom-purple via-kingdom-accent/30 to-kingdom-purple border-b border-kingdom-glow/30 px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2 mb-1 flex-wrap">
          <PlayerNameBadges
            name={hero.name}
            guildName={hero.guild_name}
            vipSubscriptionActive={hero.premium?.vip.subscription_active}
            proActive={hero.premium?.pro.contract_active}
            size="md"
            nameClassName="font-bold font-game text-lg"
          />
          <button
            type="button"
            onClick={() => setShowRename(true)}
            className="text-[10px] uppercase tracking-wide text-kingdom-glow border border-kingdom-glow/40 rounded-full px-2 py-0.5 hover:bg-kingdom-glow/10"
          >
            Rename
          </button>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-gray-400">Battle Rating</div>
        <div className="text-3xl font-bold font-game text-gold-300 tabular-nums leading-tight">
          <FormattedNumber value={hero.battle_rating ?? 0} mode="exact" />
        </div>
      </div>

      {/* ── Hero portrait with gear slots flanking ── */}
      <div className="relative bg-gradient-to-b from-kingdom-dark to-kingdom-purple overflow-hidden min-h-[240px]">
        <img
          src={heroImageSrc}
          alt={hero.hero_class}
          className="absolute left-1/2 -translate-x-1/2 bottom-0 max-h-[280px] w-auto object-contain object-bottom z-[1] pointer-events-none select-none"
        />
        <AdminAssetPath
          path={heroImageSrc}
          className="absolute left-1/2 -translate-x-1/2 bottom-1 z-[3] max-w-[90%] text-center pointer-events-auto"
        />
        <EquipmentPanel
          equipped={equipped}
          heroClass={hero.hero_class}
          onSlotClick={(slot) => setSelectedSlot(slot)}
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-kingdom-dark to-transparent z-[2] pointer-events-none" />
        <div className="absolute top-3 right-3 bg-kingdom-accent/80 text-white text-xs font-bold px-2 py-1 rounded-lg backdrop-blur-sm z-10">
          {hero.hero_class}
        </div>
      </div>

      <div className="p-4 space-y-4">
        <h2 className="text-xl font-bold font-game text-center text-gold-400">🦸 Hero</h2>

        <HeroCard hero={hero} />

        {/* Skill tree entry */}
        <button
          onClick={() => setScreen("skills")}
          className="w-full bg-gradient-to-r from-kingdom-accent/80 to-kingdom-glow/60 rounded-xl border border-kingdom-light p-4 flex items-center justify-between hover:opacity-90 transition-opacity"
        >
          <div className="text-left">
            <div className="font-bold text-white">🌳 Skill Tree</div>
            <div className="text-xs text-gray-300 mt-0.5">
              Active skills & passives
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-400">Points</div>
            <div className="text-kingdom-glow font-bold text-lg">{hero.skill_points}</div>
          </div>
        </button>

        {/* Astrals */}
        <button
          onClick={() => setScreen("astral")}
          className="w-full bg-gradient-to-r from-indigo-900/80 to-violet-800/60 rounded-xl border border-indigo-500/40 p-4 flex items-center justify-between hover:opacity-90 transition-opacity"
        >
          <div className="text-left">
            <div className="font-bold text-white">✦ Astrals</div>
            <div className="text-xs text-gray-300 mt-0.5">
              {hero.level >= 25 ? "Gather, equip & sell" : `Unlocks at level 25`}
            </div>
          </div>
          <div className="flex gap-1.5 items-center pointer-events-none">
            {astralState?.equipped_slots
              .filter((s) => s.astral)
              .slice(0, 4)
              .map((s) => (
                <AstralChip key={s.slot} astral={s.astral!} size="sm" />
              ))}
            {(!astralState || astralState.equipped_slots.every((s) => !s.astral)) && (
              <span className="text-gray-500 text-xs">—</span>
            )}
          </div>
        </button>

        {/* Skill icons reference */}
        <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-3">
          <h3 className="font-bold text-white text-sm mb-2">Skills</h3>
          <div className="grid grid-cols-4 gap-1.5">
            {availableSkills.map((skill) => (
              <div key={skill} className="flex flex-col items-center gap-1 min-w-0">
                <SkillIcon
                  heroClass={hero.hero_class}
                  skill={skill}
                  className="w-10 h-10"
                  alt={skillLabel(skill)}
                />
                <span className="text-[9px] text-gray-500 text-center leading-tight w-full line-clamp-2">
                  {skillLabel(skill)}
                </span>
                <AdminAssetPath
                  path={getSkillIconPath(hero.hero_class, skill)}
                  className="text-center w-full px-0.5"
                />
              </div>
            ))}
          </div>
        </div>

      {/* Rotation editor */}
      <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-bold text-white">Skill Rotation</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {rotation.length}/{slotCapacity} slots
              {slotCapacity < slotsMax && ` · max ${slotsMax}`}
            </p>
          </div>
          {!editingRotation ? (
            <button
              onClick={() => {
                const levels = hero.skill_levels ?? {};
                setRotation(
                  hero.rotation.filter((s) => s !== "BASIC" && (levels[s] ?? 0) >= 1),
                );
                setEditingRotation(true);
              }}
              className="text-kingdom-glow text-sm shrink-0"
            >
              Edit
            </button>
          ) : (
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => setEditingRotation(false)}
                className="text-gray-400 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveRotation}
                disabled={saving || rotation.length === 0}
                className="text-emerald-400 text-sm font-bold disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap min-h-10">
          {rotation.map((skill, i) => (
            <RotationSkillChip
              key={`${skill}-${i}`}
              skill={skill}
              label={skillLabel(skill)}
              heroClass={hero.hero_class}
              editing={editingRotation}
              onRemove={editingRotation ? () => removeFromRotation(i) : undefined}
            />
          ))}
          {editingRotation &&
            Array.from({ length: Math.max(0, slotCapacity - rotation.length) }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="w-[4.75rem] h-[3.75rem] rounded-lg border border-dashed border-gray-600/80 bg-kingdom-dark/40"
              />
            ))}
          {rotation.length === 0 && !editingRotation && (
            <span className="text-gray-500 text-sm">Tap Edit to build your rotation</span>
          )}
        </div>

        {editingRotation && (
          <div className="space-y-2 pt-1">
            <div className="text-xs text-gray-400">
              Tap a skill to add ({rotation.length}/{slotCapacity}):
            </div>
            <div className="grid grid-cols-2 gap-2">
              {rotationPickerSkills.map((skill) => (
                <button
                  key={skill}
                  onClick={() => addToRotation(skill)}
                  disabled={rotation.length >= slotCapacity}
                  className="text-left p-2 rounded-lg bg-kingdom-dark border border-kingdom-mid hover:border-kingdom-glow transition-all disabled:opacity-40 flex items-center gap-2"
                >
                  <SkillIcon
                    heroClass={hero.hero_class}
                    skill={skill}
                    className="w-9 h-9"
                    alt={skillLabel(skill)}
                  />
                  <span className="font-bold text-kingdom-glow text-xs leading-tight line-clamp-2">
                    {skillLabel(skill)}
                  </span>
                </button>
              ))}
            </div>

            {canExpandSlots && (
              <button
                onClick={expandRotationSlots}
                disabled={expanding || !canAffordExpand}
                className={clsx(
                  "w-full mt-1 py-2.5 rounded-xl border text-sm font-bold transition-all",
                  canAffordExpand && !expanding
                    ? "border-gold-500/50 bg-gold-500/10 text-gold-300 hover:bg-gold-500/20"
                    : "border-gray-700 bg-gray-800/50 text-gray-500 cursor-not-allowed",
                )}
              >
                {expanding
                  ? "Expanding..."
                  : canAffordExpand
                    ? `+${upgradeIncrement} slots · 💎 ${upgradeCost}`
                    : `Need 💎 ${upgradeCost} (${diamonds} available)`}
              </button>
            )}
          </div>
        )}
      </div>

      <AutoBattleSettingsPanel
        mode={autoBattle.mode}
        promptOnStart={autoBattle.prompt_on_start}
        tacticalSkills={autoBattle.tactical_skills}
        learnedSkills={rotationPickerSkills}
        skillLabel={(skill) => skillLabel(skill as SkillName)}
        modeEntitlements={resolveAutoBattleEntitlements(hero)}
        onNeedPremium={() => setScreen("premium")}
        onChange={persistAutoBattle}
      />

      </div>{/* /p-4 */}

      {selectedSlot && (
        <ItemDetailModal
          item={selectedSlot.item}
          slotLabel={selectedSlot.slot_label}
          heroClass={hero.hero_class}
          gems={overview?.gems ?? []}
          socketUnlockCost={overview?.socket_unlock_cost_diamonds ?? 100}
          diamonds={balance?.diamonds ?? 0}
          onClose={() => setSelectedSlot(null)}
          actionLoading={actionLoading}
          onUnequip={
            selectedSlot.item
              ? () =>
                  applyInventory(() => inventoryApi.unequip(selectedSlot.slot), {
                    keepSlot: selectedSlot.slot,
                  })
              : undefined
          }
          onUpgrade={
            selectedSlot.item && selectedSlot.item.max_enhance > 0
              ? (luckLevel) => {
                  const it = selectedSlot.item!;
                  if (it.enhance_level >= it.max_enhance) return;
                  applyUpgrade(it.id, luckLevel, selectedSlot.slot);
                }
              : undefined
          }
          luckBonusPerLevel={blacksmithConfig?.gem_craft?.luck_bonus_per_level ?? 0.1}
          onUnlockSocket={
            selectedSlot.item &&
            selectedSlot.item.sockets_unlocked < selectedSlot.item.template.max_sockets
              ? () =>
                  applyInventory(() => inventoryApi.unlockSocket(selectedSlot.item!.id), {
                    keepSlot: selectedSlot.slot,
                  })
              : undefined
          }
          onSocketGem={
            selectedSlot.item
              ? (socketIndex, gemType, gemLevel) =>
                  applyInventory(
                    () =>
                      inventoryApi.socketGem(
                        selectedSlot.item!.id,
                        socketIndex,
                        gemType,
                        gemLevel,
                      ),
                    { keepSlot: selectedSlot.slot },
                  )
              : undefined
          }
          onUnsocketGem={
            selectedSlot.item
              ? (socketIndex) =>
                  applyInventory(
                    () => inventoryApi.unsocketGem(selectedSlot.item!.id, socketIndex),
                    { keepSlot: selectedSlot.slot },
                  )
              : undefined
          }
          footerContent={
            <EquipCandidatesList
              slot={selectedSlot.slot}
              items={overview?.items ?? []}
              heroClass={hero.hero_class}
              heroLevel={hero.level}
              hasEquippedItem={!!selectedSlot.item}
              onEquip={(itemId) =>
                applyInventory(() => inventoryApi.equip(itemId), { keepSlot: selectedSlot.slot })
              }
              actionLoading={actionLoading}
            />
          }
        />
      )}

      {showRename && (
        <RenameHeroModal
          currentName={hero.name}
          onClose={() => setShowRename(false)}
          onRenamed={() => void refreshHero()}
        />
      )}
    </div>
  );
}

function RotationSkillChip({
  skill,
  label,
  heroClass,
  editing,
  onRemove,
}: {
  skill: SkillName;
  label: string;
  heroClass: import("../types").HeroClass;
  editing: boolean;
  onRemove?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      disabled={!editing}
      className={clsx(
        "flex flex-col items-center gap-0.5 p-1.5 rounded-lg border w-[4.75rem] transition-all",
        editing
          ? "border-rose-500/50 bg-rose-900/20 hover:bg-rose-900/40 cursor-pointer"
          : "border-kingdom-light bg-kingdom-mid cursor-default",
      )}
    >
      <div className="relative">
        <SkillIcon heroClass={heroClass} skill={skill} className="w-9 h-9" alt={label} />
        {editing && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-600 text-white text-[9px] leading-4">
            ✕
          </span>
        )}
      </div>
      <span className="text-[9px] text-center leading-tight line-clamp-2 text-kingdom-glow px-0.5">
        {label}
      </span>
    </button>
  );
}
